/**
 * V1 → V5.10: 공휴일 관리 훅
 *
 * **기능:**
 * - useHolidays: 공휴일 목록 조회
 * - useHolidayMutations: 공휴일 CRUD 뮤테이션
 *   - upsertHoliday, archiveHoliday, seedNationalHolidays
 *   - syncNationalHolidaysKR: V3 한국 공휴일 API 동기화
 *   - syncNationalHolidaysCN: V3 중국 공휴일 JSON 동기화
 * - useHolidaySyncLogs: V4 동기화 로그 조회
 *
 * **V5.10 변경:**
 * - syncNationalHolidaysKR 에러 핸들링 개선
 * - FirebaseError code별 사용자 친화적 메시지 반환
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import { functions } from '../../firebase/config';

// ============================================================================
// V5.10: Firebase Error 코드 → 사용자 메시지 매핑
// ============================================================================

/**
 * Firebase Functions 에러 코드를 사용자 친화적 메시지로 변환
 */
function getHolidaySyncErrorMessage(err: unknown): string {
  if (err instanceof FirebaseError) {
    const code = err.code?.replace('functions/', '') || 'unknown';
    const serverMessage = err.message || '';

    switch (code) {
      case 'unauthenticated':
        return '로그인이 필요합니다. 다시 로그인해주세요.';
      case 'permission-denied':
        return '공휴일 동기화 권한이 없습니다. 관리자에게 문의하세요.';
      case 'failed-precondition':
        // 서비스키 미설정 등
        return serverMessage || '서비스 설정이 완료되지 않았습니다. 관리자에게 문의하세요.';
      case 'unavailable':
        // 외부 API 호출 실패
        return '공공데이터포털 API에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.';
      case 'internal':
        return '서버 내부 오류가 발생했습니다. 문제가 지속되면 관리자에게 문의하세요.';
      case 'deadline-exceeded':
        return '요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
      default:
        return serverMessage || `동기화 중 오류가 발생했습니다. (${code})`;
    }
  }

  if (err instanceof Error) {
    return err.message || '알 수 없는 오류가 발생했습니다.';
  }

  return '알 수 없는 오류가 발생했습니다.';
}

/**
 * Firebase Functions 에러 코드 추출
 */
function getErrorCode(err: unknown): string {
  if (err instanceof FirebaseError) {
    return err.code?.replace('functions/', '') || 'unknown';
  }
  return 'unknown';
}

// ============================================================================
// 타입 정의
// ============================================================================

export type HolidayType = 'national' | 'company' | 'temporary';
export type HolidaySource = 'seed' | 'manual';
export type HolidayStatus = 'active' | 'archived';
export type CompanyCode = 'KR' | 'CN';

export interface Holiday {
  id: string;
  companyCode: CompanyCode;
  date: string;          // 'YYYY-MM-DD'
  name: string;
  type: HolidayType;
  source: HolidaySource;
  status: HolidayStatus;
  createdAt: any;
  createdBy: string;
  updatedAt?: any;
  updatedBy?: string;
}

interface GetHolidaysResponse {
  holidays: Holiday[];
}

interface UpsertHolidayResponse {
  success: boolean;
  holidayId: string;
}

interface ArchiveHolidayResponse {
  success: boolean;
}

interface SeedHolidaysResponse {
  success: boolean;
  createdCount: number;
  skippedCount: number;
  yearRange: string;
}

// V3: 동기화 응답 타입
interface SyncHolidaysResponse {
  success: boolean;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  year: number;
  companyCode: CompanyCode;
}

// V5.12: 복사 응답 타입
interface CopyHolidaysResponse extends SyncHolidaysResponse {
  fromYear: number;
  toYear: number;
}

// V4: 동기화 로그 타입
export type SyncStatus = 'success' | 'partial' | 'fail';
export type TriggeredBy = 'scheduler' | 'manual';

export interface YearSyncResult {
  year: number;
  success: boolean;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  error?: string;
}

export interface HolidaySyncLog {
  id: string;
  companyCode: CompanyCode;
  years: number[];
  status: SyncStatus;
  results: YearSyncResult[];
  totalCreatedCount: number;
  totalUpdatedCount: number;
  totalSkippedCount: number;
  errors: string[];
  startedAt: any;
  finishedAt: any;
  triggeredBy: TriggeredBy;
  region: string;
}

interface GetSyncLogsResponse {
  logs: HolidaySyncLog[];
}

// ============================================================================
// useHolidays - 공휴일 목록 조회
// ============================================================================

/**
 * 공휴일 목록 조회 훅
 *
 * @param companyCode - 회사 코드 ('KR' | 'CN' | null)
 * @param year - 연도 (기본값: 현재 연도)
 */
export function useHolidays(companyCode: CompanyCode | null, year?: number) {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchHolidays = useCallback(async () => {
    if (!companyCode) return;
    if (!functions) {
      setError(new Error('Firebase functions not initialized'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getHolidaysFn = httpsCallable<
        { companyCode: CompanyCode; year?: number },
        GetHolidaysResponse
      >(functions, 'getHolidays');

      const result = await getHolidaysFn({ companyCode, year });
      setHolidays(result.data.holidays);
    } catch (err: any) {
      console.error('[useHolidays] 조회 실패:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [companyCode, year]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  return {
    holidays,
    isLoading,
    error,
    refetch: fetchHolidays,
  };
}

// ============================================================================
// V5.13: useCalendarHolidays - 캘린더용 공휴일 조회 (일반 직원 허용)
// ============================================================================

/**
 * V5.13: 캘린더용 공휴일 조회 훅
 *
 * 일반 직원도 근로일정 캘린더에서 공휴일을 볼 수 있도록 하는 읽기 전용 훅.
 * getCalendarHolidays callable을 호출하며, AdminLike 역할 없이도 본인 회사 범위 내 조회 가능.
 *
 * **권한:**
 * - 인증 필수
 * - 본인 회사(companyCode) 범위만 조회 가능
 * - permission-denied 발생 시 빈 배열 반환 (에러 UI 노출 안 함)
 *
 * @param companyCode - 회사 코드 ('KR' | 'CN' | null)
 * @param year - 연도 (기본값: 현재 연도)
 */
export function useCalendarHolidays(companyCode: CompanyCode | null, year?: number) {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHolidays = useCallback(async () => {
    if (!companyCode) {
      setHolidays([]);
      return;
    }
    if (!functions) {
      console.error('[useCalendarHolidays] Firebase functions not initialized');
      setHolidays([]);
      return;
    }

    setIsLoading(true);

    try {
      const getCalendarHolidaysFn = httpsCallable<
        { companyCode: CompanyCode; year?: number },
        GetHolidaysResponse
      >(functions, 'getCalendarHolidays');

      const result = await getCalendarHolidaysFn({ companyCode, year });
      setHolidays(result.data.holidays);
    } catch (err: unknown) {
      // permission-denied 등 에러 시 빈 배열 처리 (캘린더에서는 에러 UI 불필요)
      const errCode = (err as FirebaseError)?.code?.replace('functions/', '') || '';
      if (errCode === 'permission-denied') {
        // permission-denied: 본인 회사 아님 (정상 처리)
      } else {
        console.error('[useCalendarHolidays] 조회 실패:', err);
      }
      setHolidays([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyCode, year]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  return {
    holidays,
    isLoading,
    refetch: fetchHolidays,
  };
}

// ============================================================================
// useHolidayMutations - CRUD 뮤테이션
// ============================================================================

/**
 * 공휴일 CRUD 뮤테이션 훅
 */
export function useHolidayMutations() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 공휴일 추가/수정
   */
  const upsertHoliday = useCallback(
    async (data: {
      companyCode: CompanyCode;
      date: string;
      name: string;
      type: HolidayType;
      holidayId?: string; // V238: 수정 모드 시 기존 문서 ID
    }): Promise<UpsertHolidayResponse> => {
      if (!functions) {
        throw new Error('Firebase functions not initialized');
      }

      setIsLoading(true);
      setError(null);

      try {
        const upsertFn = httpsCallable<typeof data, UpsertHolidayResponse>(
          functions,
          'upsertHoliday'
        );
        const result = await upsertFn(data);
        return result.data;
      } catch (err: any) {
        console.error('[useHolidayMutations] upsert 실패:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * 공휴일 삭제 (soft delete)
   */
  const archiveHoliday = useCallback(
    async (holidayId: string): Promise<ArchiveHolidayResponse> => {
      if (!functions) {
        throw new Error('Firebase functions not initialized');
      }

      setIsLoading(true);
      setError(null);

      try {
        const archiveFn = httpsCallable<{ holidayId: string }, ArchiveHolidayResponse>(
          functions,
          'archiveHoliday'
        );
        const result = await archiveFn({ holidayId });
        return result.data;
      } catch (err: any) {
        console.error('[useHolidayMutations] archive 실패:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * 국가 공휴일 시드 데이터 생성
   */
  const seedNationalHolidays = useCallback(
    async (data: {
      companyCode: CompanyCode;
      fromYear?: number;
      toYear?: number;
    }): Promise<SeedHolidaysResponse> => {
      if (!functions) {
        throw new Error('Firebase functions not initialized');
      }

      setIsLoading(true);
      setError(null);

      try {
        const seedFn = httpsCallable<typeof data, SeedHolidaysResponse>(
          functions,
          'seedNationalHolidays'
        );
        const result = await seedFn(data);
        return result.data;
      } catch (err: any) {
        console.error('[useHolidayMutations] seed 실패:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * V5.10: 한국 국가 공휴일 API 동기화
   *
   * 에러 발생 시 FirebaseError 코드에 따라 사용자 친화적 메시지 제공
   */
  const syncNationalHolidaysKR = useCallback(
    async (year?: number): Promise<SyncHolidaysResponse> => {
      if (!functions) {
        throw new Error('Firebase functions not initialized');
      }

      setIsLoading(true);
      setError(null);

      try {
        const syncFn = httpsCallable<{ year?: number }, SyncHolidaysResponse>(
          functions,
          'syncNationalHolidaysKR'
        );
        const result = await syncFn({ year });
        return result.data;
      } catch (err: unknown) {
        const errorCode = getErrorCode(err);
        const userMessage = getHolidaySyncErrorMessage(err);

        console.error('[useHolidayMutations] syncKR 실패:', {
          code: errorCode,
          message: userMessage,
          originalError: err,
        });

        // 사용자 친화적 메시지를 포함한 에러 생성
        const enhancedError = new Error(userMessage) as Error & { code?: string };
        enhancedError.code = errorCode;

        setError(enhancedError);
        throw enhancedError;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * V3: 중국 국가 공휴일 JSON 동기화
   */
  const syncNationalHolidaysCN = useCallback(
    async (year?: number): Promise<SyncHolidaysResponse> => {
      if (!functions) {
        throw new Error('Firebase functions not initialized');
      }

      setIsLoading(true);
      setError(null);

      try {
        const syncFn = httpsCallable<{ year?: number }, SyncHolidaysResponse>(
          functions,
          'syncNationalHolidaysCN'
        );
        const result = await syncFn({ year });
        return result.data;
      } catch (err: any) {
        console.error('[useHolidayMutations] syncCN 실패:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * V5.12: 작년 공휴일 복사
   *
   * @param companyCode - 회사 코드
   * @param fromYear - 복사 원본 연도 (기본: toYear - 1)
   * @param toYear - 복사 대상 연도 (기본: 현재 연도)
   */
  const copyHolidaysFromYear = useCallback(
    async (data: {
      companyCode: CompanyCode;
      fromYear?: number;
      toYear?: number;
    }): Promise<CopyHolidaysResponse> => {
      if (!functions) {
        throw new Error('Firebase functions not initialized');
      }

      setIsLoading(true);
      setError(null);

      try {
        const copyFn = httpsCallable<typeof data, CopyHolidaysResponse>(
          functions,
          'copyHolidaysFromYear'
        );
        const result = await copyFn(data);
        return result.data;
      } catch (err: unknown) {
        const errorCode = getErrorCode(err);
        const userMessage = getHolidaySyncErrorMessage(err);

        console.error('[useHolidayMutations] copy 실패:', {
          code: errorCode,
          message: userMessage,
          originalError: err,
        });

        const enhancedError = new Error(userMessage) as Error & { code?: string };
        enhancedError.code = errorCode;

        setError(enhancedError);
        throw enhancedError;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    upsertHoliday,
    archiveHoliday,
    seedNationalHolidays,
    syncNationalHolidaysKR,
    syncNationalHolidaysCN,
    copyHolidaysFromYear, // V5.12
    isLoading,
    error,
  };
}

// ============================================================================
// V4: useHolidaySyncLogs - 동기화 로그 조회
// ============================================================================

/**
 * V4 → V5.12.2: 동기화 로그 조회 훅
 *
 * @param companyCode - 회사 코드 (null이면 조회하지 않음)
 * @param limit - 조회 건수 (기본 10)
 *
 * V5.12.2: permission-denied 에러를 isForbidden 상태로 명시적 반환
 */
export function useHolidaySyncLogs(companyCode: CompanyCode | null, limit: number = 10) {
  const [logs, setLogs] = useState<HolidaySyncLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);

  const fetchLogs = useCallback(async () => {
    // companyCode가 null이면 조회하지 않음 (초기화 전 API 호출 방지)
    if (!companyCode) {
      setLogs([]);
      setIsForbidden(false);
      return;
    }

    if (!functions) {
      console.error('[useHolidaySyncLogs] Firebase functions not initialized');
      return;
    }

    setIsLoading(true);
    setIsForbidden(false);

    try {
      const getLogsFn = httpsCallable<
        { companyCode: CompanyCode; limit?: number },
        GetSyncLogsResponse
      >(functions, 'getHolidaySyncLogs');

      const result = await getLogsFn({ companyCode, limit });
      setLogs(result.data.logs);
    } catch (err: unknown) {
      // V5.12.2: permission-denied는 에러가 아닌 권한 없음 상태로 처리
      const errCode = (err as FirebaseError)?.code?.replace('functions/', '') || '';
      if (errCode === 'permission-denied') {
        setLogs([]);
        setIsForbidden(true);
      } else {
        console.error('[useHolidaySyncLogs] 조회 실패:', err);
        setLogs([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [companyCode, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    isLoading,
    isForbidden,
    refetch: fetchLogs,
  };
}
